import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useVault } from '../context/VaultContext';
import type {
  UpdatePublicProfileInput,
  UpdateListingStatusInput,
  CreateWantedItemInput,
  UpdateWantedItemInput,
  CreateOfferInput,
} from '@siliconvault/shared';

// ─── Profiles ───

export function usePublicProfile(slug: string | undefined) {
  return useQuery({
    queryKey: ['profile', slug],
    queryFn: () => apiClient.getPublicProfile(slug!),
    enabled: !!slug,
  });
}

export function useProfileItems(slug: string | undefined, query: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['profile', slug, 'items', query],
    queryFn: () => apiClient.getProfileItems(slug!, query),
    enabled: !!slug,
  });
}

export function useProfileWanted(slug: string | undefined) {
  return useQuery({
    queryKey: ['profile', slug, 'wanted'],
    queryFn: () => apiClient.getProfileWanted(slug!),
    enabled: !!slug,
  });
}

export function useUpdatePublicProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdatePublicProfileInput) => apiClient.updatePublicProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File | Blob) => apiClient.uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useDeleteAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.deleteAvatar(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

// ─── Listing Status ───

export function useUpdateListingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateListingStatusInput }) =>
      apiClient.updateListingStatus(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['showroom'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

// ─── Wanted Items ───

export function useWantedItems() {
  const { activeVaultId } = useVault();
  return useQuery({
    queryKey: ['wanted', activeVaultId],
    queryFn: () => apiClient.listWantedItems(activeVaultId ?? undefined),
  });
}

export function useCreateWantedItem() {
  const { activeVaultId } = useVault();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWantedItemInput) => apiClient.createWantedItem(data, activeVaultId ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wanted'] });
    },
  });
}

export function useUpdateWantedItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWantedItemInput }) =>
      apiClient.updateWantedItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wanted'] });
    },
  });
}

export function useDeleteWantedItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteWantedItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wanted'] });
    },
  });
}

// ─── Offers ───

export function useOffers(query: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['offers', query],
    queryFn: () => apiClient.listOffers(query),
  });
}

export function useOffer(id: string | undefined) {
  return useQuery({
    queryKey: ['offers', id],
    queryFn: () => apiClient.getOffer(id!),
    enabled: !!id,
  });
}

export function useCreateOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOfferInput) => apiClient.createOffer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
}

export function useRespondToOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'decline' }) =>
      apiClient.respondToOffer(id, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
}

export function useWithdrawOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.withdrawOffer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
}

export function useDismissOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.dismissOffer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
}
